<template>
    <div class="flex flex-col" :class="tonalThemeEnabled ? 'gap-5' : 'gap-4'">
        <template v-for="(events, date) in calendarEvents" :key="date">
            <div v-if="tonalThemeEnabled" :data-date="date" class="flex flex-col flex-1">
                <div class="px-2.5 pb-2 scaffold-details-small text-ink-muted sticky top-0 z-10" :class="showHeaderBackground ? 'bg-surface-raised' : 'bg-transparent'">
                    {{ formattedDay(date) }}&nbsp;{{ formattedDate(date) }}
                </div>
                <div class="event-list flex flex-col flex-1 w-full">
                    <template v-for="(event, eventIndex) in events" :key="event.event_id">
                        <CalEventCollapsible :event="event" :divider="eventIndex < events.length - 1" @error="handleError" />
                    </template>
                </div>
            </div>
            <div v-else :data-date="date" class="calendar-events flex flex-col-reverse gap-2 flex-1" :class="{ 'opacity-75': isBeforeToday(date) }">
                <div class="flex flex-col gap-2.5 flex-1 w-full">
                    <template v-for="event in events" :key="event.event_id">
                        <CalEventCollapsible :event="event" :calendar-color="calendarColor(event)" :button-color="buttonColor(event)" @error="handleError" />
                    </template>
                </div>
                <div class="pt-2 text-ink-muted sticky top-0" :class="[isToday(date) ? 'body-small-semibold' : 'body-small', showHeaderBackground ? 'bg-white' : 'bg-transparent']">
                    {{ formattedDay(date) }}&nbsp;{{ formattedDate(date) }}
                </div>
            </div>
        </template>
        <BaseToast v-model:open="isErrorToastOpen" :description="errorMessage" />
    </div>
</template>

<script setup lang="ts">
import { isBeforeToday, isToday } from "/js/dateUtils";
import type { ProcessedCalendarEvent } from "~vue/types/calendar_integration";
import { formatDate } from "~vue/utils/formatDate";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { DateTime } from "luxon";
import { ref } from "vue";

import BaseToast from "../BaseToast.vue";
import { CALENDAR_BUTTON_COLOR_GROUP, CALENDAR_BUTTON_COLOR_ONE_ON_ONE, CALENDAR_DIVIDER_COLOR_GROUP, CALENDAR_DIVIDER_COLOR_ONE_ON_ONE } from "./calendarConstants";
import CalEventCollapsible from "./CalEventCollapsible.vue";

interface Props {
    calendarEvents: Record<string, ProcessedCalendarEvent[]>;
    showHeaderBackground?: boolean;
}

withDefaults(defineProps<Props>(), {
    showHeaderBackground: true,
});

// TODO: Remove the legacy calendar branch once the tonal theme becomes the default.

const calendarColor = (event: ProcessedCalendarEvent) => {
    return event.attendees.length === 2 ? CALENDAR_DIVIDER_COLOR_ONE_ON_ONE : CALENDAR_DIVIDER_COLOR_GROUP;
};

const buttonColor = (event: ProcessedCalendarEvent) => {
    return event.attendees.length === 2 ? CALENDAR_BUTTON_COLOR_ONE_ON_ONE : CALENDAR_BUTTON_COLOR_GROUP;
};

const formattedDay = (date: string) => {
    const dateObj = DateTime.fromISO(date);
    const today = DateTime.now().startOf("day");

    // Check if it's today
    if (dateObj.hasSame(today, "day")) {
        return "Today";
    }

    // Check if it's tomorrow
    const tomorrow = today.plus({ days: 1 });
    if (dateObj.hasSame(tomorrow, "day")) {
        return "Tomorrow";
    }

    // Otherwise return the day name
    return formatDate(dateObj, "weekday");
};

const formattedDate = (date: string) => formatDate(date, tonalThemeEnabled.value ? "month-day-long" : "month-day");

const errorMessage = ref("");
const isErrorToastOpen = ref(false);
const handleError = (errorMsg: string) => {
    errorMessage.value = errorMsg;
    isErrorToastOpen.value = true;
};
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

.calendar-events :deep(.trigger) {
    @apply w-full text-left;
}

/* this hides the divider lines when focused, hovered or active */
.event-list .event-container:has(+ .event-container:hover, + .event-container :focus-visible, + .event-container.is-active) :deep(.event)::after {
    @apply hidden;
}
</style>
